import {
  CommonHelperFunctions,
  ICreateButtonRequest,
  IScreenDesignDto,
  IScreenDesignHistoryDTO,
  IScreenDesignInfoDto
} from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { FireBaseService } from "../firebase/firebase";
import { KMainFunctions } from "../shared/utility/main-functions";
import { UserService } from "./user-service";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export class ScreenService {
  private static projectIdCache = new Map<string, string>();

  public static async Create(screen: IScreenDesignDto): Promise<AxiosResponse<any>> {
    try {
      const res = await Axios.post<IScreenDesignDto, AxiosResponse>(`${backendUrl}/screendesign/create`, screen);
      const firebase = new FireBaseService();
      firebase.logEvent("screen_create");
      await UserService.GetUserInfoForce();
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAllByProjectId(id: string): Promise<AxiosResponse<IScreenDesignDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/screendesign/byprojectid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetWorkflowScreenActionDefinitionIds(id: string): Promise<AxiosResponse<string[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/screendesign/workflowscreenactionids/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetScreenNamesByProjectId(id: string): Promise<AxiosResponse<{ id: string; name: string }[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/screendesign/names/byprojectid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetScreenInputsByScreenId(
    id: string
  ): Promise<AxiosResponse<{ name: string; typeName: string; cardinality: number }[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/screendesign/inputs/byscreenid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Get(
    id: string,
    isLoadingAsMasterOfScreen: boolean = false
  ): Promise<AxiosResponse<IScreenDesignDto>> {
    const url: URL = new URL(`${backendUrl}/screendesign/getById/${id}`);

    if (isLoadingAsMasterOfScreen) {
      url.searchParams.append("isLoadingAsMasterOfScreen", "true");
    }

    try {
      return await Axios.get<string, AxiosResponse>(url.toString(), {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache"
        }
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectSdListBySdId(id: string): Promise<AxiosResponse<IScreenDesignInfoDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/screendesign/projectsdlistbysd/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Update(
    id: string,
    obj: IScreenDesignDto,
    isSystemMigration: boolean = false
  ): Promise<AxiosResponse<IScreenDesignDto>> {
    try {
      const url: URL = new URL(`${backendUrl}/screendesign/${id}`);

      // Tag on-load component-schema upgrades so the branching save-interceptor applies them
      // to the branch snapshot silently (no staged change / no merge conflict).
      const config = isSystemMigration ? { headers: { "X-Kuika-Write-Origin": "schema-migration" } } : undefined;
      return await Axios.put<IScreenDesignDto, AxiosResponse>(url.toString(), obj, config);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Delete(id: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.delete<null>(`${backendUrl}/screendesign/${id}`);
      await UserService.GetUserInfoForce();
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async DeleteCurrentScreen(id: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.delete<null>(`${backendUrl}/screendesign/current/${id}`);
      await UserService.GetUserInfoForce();
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetSdVersions(id: string): Promise<AxiosResponse<IScreenDesignHistoryDTO[]>> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/screendesign/sdversions/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetSdByHistoryId(id: string): Promise<AxiosResponse<IScreenDesignDto>> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/screendesign/getsdbyhistoryid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async DuplicateScreen(id: string): Promise<AxiosResponse> {
    try {
      const res = Axios.post<string, AxiosResponse>(`${backendUrl}/screendesign/duplicatescreen/${id}`);
      await UserService.GetUserInfoForce();
      return await res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Leave(id: string): Promise<AxiosResponse<IScreenDesignDto>> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/screendesign/leave/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async LeaveScreenByUserId(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/screendesign/leavescreenbyuserid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * `isReport` filters the feed by screen type — true for reports only, false to exclude them,
   * omitted for both. It is a server-side filter because `count` caps the rows there: filtering
   * after the fact would come up short whenever the other type crowds the top of the list.
   */
  public static async GetRecentlyOpenedScreens(
    userId: string,
    count: number,
    projectId: string,
    isReport?: boolean
  ): Promise<AxiosResponse> {
    try {
      return await Axios.get<any, AxiosResponse>(
        `${backendUrl}/screendesign/getrecentlyopenedscreens/${userId}?count=${count}${
          projectId ? `&projectId=${projectId}` : ""
        }${isReport === undefined ? "" : `&isReport=${isReport}`}`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async CreateButtonComponent(request: ICreateButtonRequest): Promise<AxiosResponse<IScreenDesignDto>> {
    try {
      return await Axios.post<any, AxiosResponse<IScreenDesignDto>>(
        `${backendUrl}/screendesign/createbuttoncomponent`,
        request
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateButtonComponent(
    request: ICreateButtonRequest & { id: string }
  ): Promise<AxiosResponse<IScreenDesignDto>> {
    try {
      return await Axios.post<any, AxiosResponse<IScreenDesignDto>>(
        `${backendUrl}/screendesign/updatebuttoncomponent`,
        request
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async getProjectIdFromScreenId(screenId: string): Promise<string> {
    // Check cache first
    const cached = this.projectIdCache.get(screenId);
    if (cached) {
      return cached;
    }

    try {
      const response = await Axios.get<string>(`${backendUrl}/screendesign/getprojectid/${screenId}`);
      // Cache the result
      this.projectIdCache.set(screenId, response.data);
      return response.data;
    } catch (error) {
      console.error("Error getting project ID from screen ID:", error);
      throw error;
    }
  }

  /**
   * Records a screen's app when the caller already knows it — the header's app switcher lists
   * apps with their entry screen — so the designer route can tell an app switch without a round trip.
   */
  public static rememberProjectOfScreen(screenId: string, projectId: string): void {
    this.projectIdCache.set(screenId, projectId);
  }

  // Add method to clear cache when needed
  public static clearProjectIdCache() {
    this.projectIdCache.clear();
  }
}
